import { useLanguage } from "@/contexts/LanguageContext";
import { cn, formatUSD } from "@/lib/utils";
import type { TeamBill } from "@/lib/types";

// Bill summary, shared by the admin bill list (AdminBills) and the team's
// read-only bill view (TeamManagement). Two distinct blocks with DIFFERENT
// bases (see summaryNoteBasis):
//   Consumption view (windowed, reconciles exactly):
//     原价总额 -> 折扣减免 -> 折后总消耗 -> 代金券抵扣 -> 现金抵扣
//       -> 本期信用消费 (= 折后总消耗 − 代金券 − 现金, the true bottom of the
//          per-request funding split; the three segments sum to consumption)
//   Account view (net-debt increment, may differ from the block above because
//   it accounts for recharges/payments/carried-over balance):
//     应付金额
// Field order/copy follow
// docs/superpowers/specs/2026-07-06-billing-v2-bill-mockup.html.
// The discount rate is intentionally not shown here (see summaryNoteDiscount).
export default function BillSummary({ bill }: { bill: TeamBill }) {
  const { t } = useLanguage();

  const Row = ({
    label,
    value,
    negative,
    subtotal,
    emphasis,
  }: {
    label: string;
    value: string;
    negative?: boolean;
    subtotal?: boolean;
    emphasis?: boolean;
  }) => (
    <div
      className={cn(
        "flex items-center justify-between py-1.5",
        emphasis
          ? "text-sm font-semibold text-white pt-2.5 mt-1 border-t border-white/15"
          : subtotal
            ? "text-xs font-medium text-slate-200 pt-2 mt-0.5 border-t border-white/10"
            : "text-xs text-slate-300",
      )}
    >
      <span>{label}</span>
      <span className={cn("font-mono", negative && "text-emerald-400")}>
        {negative ? "-" : ""}
        {value}
      </span>
    </div>
  );

  // 本期信用消费 = 折后总消耗 − 代金券抵扣 − 现金抵扣 (= Σ credit segment).
  // The three funding segments sum exactly to consumption, so this is the
  // reconcilable bottom of the consumption block. Rounded to the schema's 9
  // decimal places to shed float subtraction dust before display. This is a
  // consumption figure, NOT the amount due (see summaryNoteBasis / the
  // net-debt basis explained there).
  const creditConsumed =
    Math.round(
      (Number(bill.total_consumption) -
        Number(bill.voucher_offset) -
        Number(bill.cash_offset)) *
        1e9,
    ) / 1e9;

  return (
    <div className="max-w-[380px] ml-auto space-y-0">
      <Row label={t("admin.bills.summaryTotalList")} value={formatUSD(bill.total_list_amount)} />
      <Row label={t("admin.bills.summaryDiscount")} value={formatUSD(bill.discount_total)} negative />
      <Row label={t("admin.bills.summaryConsumption")} value={formatUSD(bill.total_consumption)} />
      <Row label={t("admin.bills.summaryVoucherOffset")} value={formatUSD(bill.voucher_offset)} negative />
      <Row label={t("admin.bills.summaryCashOffset")} value={formatUSD(bill.cash_offset)} negative />
      <Row label={t("admin.bills.summarySubtotalCredit")} value={formatUSD(creditConsumed)} subtotal />
      <Row label={t("admin.bills.summaryAmountDue")} value={formatUSD(bill.amount_due)} emphasis />
      <p className="text-[10px] text-slate-500 leading-relaxed pt-3">{t("admin.bills.summaryNoteBasis")}</p>
      <p className="text-[10px] text-slate-500 leading-relaxed pt-1.5">{t("admin.bills.summaryNoteDiscount")}</p>
    </div>
  );
}
